import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Download, History, Star } from "lucide-react";
import api from "../lib/api";
import Header from "../components/dashboard/Header";

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthAgoISO = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };
const fmtDate = (iso) => { try { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

export default function CheckInHistory() {
  const [start, setStart] = useState(monthAgoISO());
  const [end, setEnd] = useState(todayISO());
  const [location, setLocation] = useState(() => new URLSearchParams(window.location.search).get("location") || "all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/daypasses/history", { params: { start, end, location } });
      setRows(data.passes || []);
    } catch (e) { setRows([]); toast.error("Check-in history couldn't load", { description: e.userMessage }); }
    finally { setLoading(false); }
  }, [start, end, location]);
  useEffect(() => { load(); }, [load]);

  const locations = useMemo(() => [...new Set(rows.map((r) => r.location).filter(Boolean))].sort(), [rows]);
  const repeatCount = rows.filter((r) => r.repeat).length;

  const exportCsv = async () => {
    try {
      const res = await api.get("/daypasses/history.csv", { params: { start, end, location }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "day-pass-checkins.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Operations</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Check-in History</h1>
            <p className="text-sm text-brand-muted mt-1">Every day-pass check-in — who, where, and when. Export for attendance & billing.</p>
          </div>
          <button onClick={exportCsv} disabled={rows.length === 0} data-testid="checkin-export"
            className="inline-flex items-center gap-2 bg-brand-ink text-brand-page px-4 py-2.5 text-sm rounded-md hover:bg-brand-primary disabled:opacity-40">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <label className="text-xs text-brand-tertiary">From <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="history-start" className="ml-1 border border-brand-border rounded-md px-2 py-1.5 text-sm bg-brand-surface text-brand-ink" /></label>
          <label className="text-xs text-brand-tertiary">To <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="history-end" className="ml-1 border border-brand-border rounded-md px-2 py-1.5 text-sm bg-brand-surface text-brand-ink" /></label>
          <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="history-location" className="border border-brand-border rounded-md px-3 py-2 text-sm bg-brand-surface text-brand-ink">
            <option value="all">All locations</option>
            {[...locations, ...(location !== "all" && !locations.includes(location) ? [location] : [])].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <span className="text-sm text-brand-tertiary flex items-center gap-3">
            <span data-testid="history-count">{rows.length} check-ins</span>
            {repeatCount > 0 && <span className="inline-flex items-center gap-1 text-brand-gold"><Star className="h-3.5 w-3.5" /> {repeatCount} repeat</span>}
          </span>
        </div>

        <div className="mt-6 bg-brand-surface border border-brand-border rounded-md overflow-hidden">
          {loading ? (
            <div className="py-16 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-brand-tertiary" data-testid="history-empty"><History className="h-6 w-6 mx-auto text-brand-tertiary mb-2" />No check-ins in this range.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-brand-tertiary border-b border-brand-border text-left">
                {["Visit", "Location", "Guest", "Contact", "Checked in", "By"].map((h) => <th key={h} className="font-medium uppercase tracking-[0.1em] text-[11px] px-4 py-2.5">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`history-row-${r.id}`} className="border-b border-brand-border/60 last:border-0 hover:bg-brand-hover">
                    <td className="px-4 py-3 text-brand-ink whitespace-nowrap">{fmtDate(r.pass_date)}</td>
                    <td className="px-4 py-3 text-brand-muted">{r.location || "—"}</td>
                    <td className="px-4 py-3 text-brand-ink">
                      <span className="flex items-center gap-1.5">{r.guest_name || "Guest"}
                        {r.repeat && <span title={`${r.visit_count} visits`} className="inline-flex items-center gap-0.5 text-[10px] text-brand-gold border border-brand-gold/40 rounded-full px-1.5"><Star className="h-2.5 w-2.5" />{r.visit_count}</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-brand-tertiary text-xs">{r.guest_email || ""}{r.phone ? ` · ${r.phone}` : ""}</td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{r.checked_in_at ? fmtDT(r.checked_in_at) : "—"}</td>
                    <td className="px-4 py-3 text-brand-tertiary text-xs">{r.checked_in_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
